// @ts-nocheck
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ChevronRight, ClipboardPenLine, Droplet, Snowflake, Store, type LucideIcon } from "lucide-react";
import {
  Select, Button, Modal, Form, Input, InputNumber, Switch,
  Typography, Space, Popconfirm, message, Tag, Drawer, Collapse,
  Tooltip, App, Dropdown,
} from "../components/admin-ui";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UnorderedListOutlined, MoreOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { MenuProps } from "../components/admin-ui";
import type { MenuItem, MenuOptionGroup, MenuOptionChoice, Stall } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { SortableMenuList } from "../components/SortableMenuList";
import { StatusBadge } from "../components/StatusBadge";
import { AdminShell } from "../layouts/AdminShell";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { formatDateTimeCompact } from "../utils/formatLocaleDate";
import {
  useAdminStalls, useUpdateStall, useAdminMenuItems,
  useDeleteMenuItem, useReorderMenuItems, useUpdateMenuItem,
  useCreateOptionGroup, useUpdateOptionGroup, useDeleteOptionGroup,
  useCreateOptionChoice, useUpdateOptionChoice, useDeleteOptionChoice,
  useAdminMenuCategories, useCreateMenuCategory, useUpdateMenuCategory, useDeleteMenuCategory,
  type MenuCategoryRow,
} from "../api/hooks";
import { tokens } from "../styles/tokens";
const { color: T } = tokens;

const { Text } = Typography;

function useViewportWidth() {
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 1440 : window.innerWidth));

  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return width;
}

/* ── Customization option group icons ── */
const GROUP_ICON_DEFS: { key: string; Icon: LucideIcon }[] = [
  { key: "sugar", Icon: Droplet },
  { key: "ice", Icon: Snowflake },
  { key: "special", Icon: ClipboardPenLine },
];

const GROUP_ICON_LABEL_KEYS: Record<string, string> = {
  sugar: "menu.groupIconSugar",
  ice: "menu.groupIconIce",
  special: "menu.groupIconSpecial",
};

function GroupIcon({ iconKey, size = 16, color = "currentColor" }: { iconKey: string; size?: number; color?: string }) {
  const match = GROUP_ICON_DEFS.find(i => i.key === iconKey);
  if (!match) return null;
  const Icon = match.Icon;
  return <Icon size={size} color={color} strokeWidth={2.4} style={{ display: "block" }} aria-hidden="true" />;
}

/* ─────────────────────────────────────────────────────────
   Option Groups Drawer
   ───────────────────────────────────────────────────────── */
function OptionGroupsDrawer({
  open, item, onClose,
}: {
  open: boolean;
  item: MenuItem | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [groupForm] = Form.useForm();
  const [choiceForm] = Form.useForm();
  const activeIconKey = Form.useWatch("icon_key", { form: groupForm, preserve: true });
  const choiceInputType = Form.useWatch("input_type", { form: choiceForm, preserve: true });
  const [editingGroup, setEditingGroup] = useState<MenuOptionGroup | null>(null);
  const [editingChoice, setEditingChoice] = useState<MenuOptionChoice | null>(null);
  const [addingGroupOpen, setAddingGroupOpen] = useState(false);
  const [addingChoiceForGroup, setAddingChoiceForGroup] = useState<string | null>(null);

  const createGroup = useCreateOptionGroup();
  const updateGroup = useUpdateOptionGroup();
  const deleteGroup = useDeleteOptionGroup();
  const createChoice = useCreateOptionChoice();
  const updateChoice = useUpdateOptionChoice();
  const deleteChoice = useDeleteOptionChoice();

  const groupIcons = useMemo(
    () =>
      GROUP_ICON_DEFS.map(def => ({
        ...def,
        label: t(GROUP_ICON_LABEL_KEYS[def.key] ?? "menu.formGroupIcon"),
      })),
    [t],
  );

  const choiceInputTypeOptions = useMemo(
    () => [
      { value: "radio", label: t("menu.choiceTypeRadio") },
      { value: "quantity", label: t("menu.choiceTypeQuantity") },
      { value: "input", label: t("menu.choiceTypeInputBox") },
      { value: "textarea", label: t("menu.choiceTypeTextarea") },
    ],
    [t],
  );

  const groups: MenuOptionGroup[] = (item as (MenuItem & { option_groups?: MenuOptionGroup[] }))?.option_groups ?? [];

  // ── Group handlers ──
  const openAddGroup = () => { setEditingGroup(null); groupForm.resetFields(); setAddingGroupOpen(true); };
  const openEditGroup = (g: MenuOptionGroup) => {
    setEditingGroup(g);
    groupForm.setFieldsValue({ name: g.name, type: g.type, min_select: g.min_select, max_select: g.max_select, sort_order: g.sort_order, icon_key: g.icon_key ?? null });
    setAddingGroupOpen(true);
  };
  const saveGroup = async () => {
    try {
      const vals = await groupForm.validateFields();
      if (editingGroup) {
        await updateGroup.mutateAsync({ id: editingGroup.id, ...vals });
      } else {
        await createGroup.mutateAsync({ itemId: item!.id, ...vals });
      }
      void message.success(t("menu.groupSaved"));
      setAddingGroupOpen(false);
    } catch { void message.error(t("menu.saveFailed")); }
  };
  const removeGroup = async (id: string) => {
    try {
      await deleteGroup.mutateAsync(id);
      void message.success(t("menu.groupDeleted"));
    } catch { void message.error(t("menu.deleteFailed")); }
  };

  // ── Choice handlers ──
  const openAddChoice = (groupId: string) => {
    setEditingChoice(null); choiceForm.resetFields(); setAddingChoiceForGroup(groupId);
  };
  const openEditChoice = (c: MenuOptionChoice) => {
    setEditingChoice(c);
    choiceForm.setFieldsValue({ label: c.label, extra_charge: parseFloat(c.extra_charge ?? "0"), sort_order: c.sort_order, input_type: c.input_type ?? "radio" });
    setAddingChoiceForGroup(c.group_id);
  };
  const saveChoice = async () => {
    try {
      const vals = await choiceForm.validateFields();
      if (editingChoice) {
        await updateChoice.mutateAsync({ id: editingChoice.id, ...vals });
      } else {
        await createChoice.mutateAsync({ groupId: addingChoiceForGroup!, ...vals });
      }
      void message.success(t("menu.choiceSaved"));
      setAddingChoiceForGroup(null);
    } catch { void message.error(t("menu.saveFailed")); }
  };
  const removeChoice = async (id: string) => {
    try {
      await deleteChoice.mutateAsync(id);
      void message.success(t("menu.choiceDeleted"));
    } catch { void message.error(t("menu.deleteFailed")); }
  };

  return (
    <Drawer
      open={open}
      title={t("menu.optionsDrawerTitle", { name: item?.name ?? "" })}
      onClose={onClose}
      width={520}
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openAddGroup}
          style={{ background: T.brandAmber, borderColor: T.brandAmber }}>
          {t("menu.addGroup")}
        </Button>
      }
    >
      {groups.length === 0 && (
        <div style={{ textAlign: "center", color: "#9CA3AF", padding: "40px 0" }}>
          {t("common.noData")}
        </div>
      )}

      <Collapse
        accordion
        items={groups.map((g) => ({
          key: g.id,
          label: (
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Text strong>{g.name}</Text>
              <Tag color={g.type === "required" ? "red" : "blue"}>
                {g.type === "required" ? t("menu.groupTypeRequired") : t("menu.groupTypeOptional")}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {g.min_select}-{g.max_select} {t("menu.groupMinSelect").toLowerCase()}
              </Text>
            </div>
          ),
          extra: (
            <Space onClick={(e) => e.stopPropagation()}>
              <Tooltip title={t("common.edit")}>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEditGroup(g)} />
              </Tooltip>
              <Popconfirm title={t("menu.confirmDeleteGroup")} onConfirm={() => void removeGroup(g.id)} okType="danger" cancelLabel={t("common.cancel")} confirmLabel={t("common.delete")}>
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Space>
          ),
          children: (
            <>
              {(g.choices ?? []).map((c) => (
                <div key={c.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "6px 8px", background: T.bg, borderRadius: 6, marginBottom: 4,
                }}>
                  <div>
                    <Text>{c.label}{c.input_type === "quantity" ? ` · ${t("menu.choiceTypeQuantity")}` : c.input_type === "input" || c.input_type === "textarea" ? ` · ${t("menu.choiceTypeInputBox")}` : ""}</Text>
                    {parseFloat(c.extra_charge ?? "0") > 0 && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>+RM {parseFloat(c.extra_charge).toFixed(2)}</Tag>
                    )}
                  </div>
                  <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEditChoice(c)} />
                    <Popconfirm title={t("menu.confirmDeleteChoice")} onConfirm={() => void removeChoice(c.id)} okType="danger" cancelLabel={t("common.cancel")} confirmLabel={t("common.delete")}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                </div>
              ))}
              <Button size="small" icon={<PlusOutlined />} onClick={() => openAddChoice(g.id)}
                style={{ marginTop: 6, borderStyle: "dashed" }}>
                {t("menu.addChoice")}
              </Button>
            </>
          ),
        }))}
      />

      {/* Add/Edit Group Modal */}
      <Modal
        open={addingGroupOpen}
        title={<span className="text-lg font-bold text-[#111827]">{editingGroup ? t("menu.editGroup") : t("menu.addGroup")}</span>}
        width={480}
        onCancel={() => setAddingGroupOpen(false)}
        onOk={() => void saveGroup()}
        okText={t("common.save")}
        okButtonProps={{ loading: createGroup.isPending || updateGroup.isPending, style: { background: T.brandAmber, borderColor: T.brandAmber } }}
        destroyOnHidden
      >
        <Form form={groupForm} layout="vertical">
          <Form.Item name="icon_key" label={t("menu.formGroupIcon")}>
            <div style={{ display: "flex", gap: 8 }}>
              {groupIcons.map(icon => {
                const active = activeIconKey === icon.key;
                return (
                  <button
                    key={icon.key}
                    type="button"
                    title={icon.label}
                    onClick={() => { groupForm.setFieldValue("icon_key", active ? null : icon.key); }}
                    style={{
                      width: 44, height: 44, borderRadius: 10, cursor: "pointer",
                      border: active ? "2px solid #D97706" : "1.5px solid #E5E7EB",
                      background: active ? T.brandLight : T.bg,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      padding: 0, lineHeight: 1,
                    }}
                  >
                    <GroupIcon iconKey={icon.key} size={18} color={active ? T.brandAmber : T.textMuted} />
                  </button>
                );
              })}
            </div>
          </Form.Item>
          <Form.Item name="name" label={t("menu.groupName")} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label={t("menu.groupType")} rules={[{ required: true }]} initialValue="optional">
            <Select options={[
              { value: "required", label: t("menu.groupTypeRequired") },
              { value: "optional", label: t("menu.groupTypeOptional") },
            ]} />
          </Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <Form.Item name="min_select" label={t("menu.groupMinSelect")} initialValue={0}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="max_select" label={t("menu.groupMaxSelect")} initialValue={1}>
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="sort_order" label={t("menu.formSortOrder")} initialValue={0}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Add/Edit Choice Modal */}
      <Modal
        open={addingChoiceForGroup !== null}
        title={<span className="text-lg font-bold text-[#111827]">{editingChoice ? t("menu.editChoice") : t("menu.addChoice")}</span>}
        width={480}
        onCancel={() => setAddingChoiceForGroup(null)}
        onOk={() => void saveChoice()}
        okText={t("common.save")}
        okButtonProps={{ loading: createChoice.isPending || updateChoice.isPending, style: { background: T.brandAmber, borderColor: T.brandAmber } }}
        destroyOnHidden
      >
        <Form form={choiceForm} layout="vertical">
          <Form.Item name="input_type" label={t("menu.choiceInputType")} initialValue="radio">
            <Select options={choiceInputTypeOptions} />
          </Form.Item>
          <div style={{ fontSize: 12, color: "#6b7280", margin: "-8px 0 12px", lineHeight: 1.5 }}>{t("menu.choiceTypeHelp")}</div>
          {choiceInputType === "textarea" ? (
            <Form.Item name="label" label={t("menu.choicePlaceholderLabel")} rules={[{ required: true }]}>
              <Input.TextArea rows={2} placeholder={t("menu.choicePlaceholderExample")} />
            </Form.Item>
          ) : (
            <Form.Item name="label" label={t("menu.choiceLabel")} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          )}
          <Form.Item name="extra_charge" label={t("menu.choiceExtraCharge")} initialValue={0}>
            <InputNumber prefix="RM" min={0} precision={2} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="sort_order" label={t("menu.formSortOrder")} initialValue={0}>
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </Drawer>
  );
}

/* ─────────────────────────────────────────────────────────
   Main MenuScreen
   ───────────────────────────────────────────────────────── */
export function MenuScreen() {
  const { t, i18n } = useTranslation();
  const { message: msg } = App.useApp();
  const { confirm: confirmDialog } = useConfirmDialog();
  const viewportWidth = useViewportWidth();
  const [stallId, setStallId] = useState<string | undefined>();
  const [optionsItemId, setOptionsItemId] = useState<string | null>(null);
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [showStallListOverlay, setShowStallListOverlay] = useState(false);

  // Stall details panel form state
  const [stallForm] = Form.useForm();
  const [stallDirty, setStallDirty] = useState(false);
  const updateStall = useUpdateStall();
  const stallIsOpenWatched = Form.useWatch("is_open", { form: stallForm, preserve: true }) as boolean | undefined;

  const { data: stallsData } = useAdminStalls();
  const { data: menuData, isLoading } = useAdminMenuItems(stallId);
  const { data: catData } = useAdminMenuCategories(stallId);
  const { data: allCatData } = useAdminMenuCategories();

  const stalls: Stall[] = stallsData?.data ?? [];
  const categories: MenuCategoryRow[] = catData?.data ?? [];
  const allCategories: MenuCategoryRow[] = allCatData?.data ?? [];
  const items: MenuItem[] = menuData?.data ?? [];

  const selectedStall = stalls.find(s => s.id === stallId) ?? null;

  // Search / category filter / drag-reorder view
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState<string | undefined>();
  const [viewMode, setViewMode] = useState<"list" | "sort">("list");
  const reorder = useReorderMenuItems(stallId);
  useEffect(() => { setSearch(""); setCatFilter(undefined); }, [stallId]);
  const itemCatIds = (it: MenuItem): string[] => (it as { categories?: Array<{ id: string }> }).categories?.map((c) => c.id) ?? [];
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (q && !it.name.toLowerCase().includes(q)) return false;
      if (catFilter === "__none__") return itemCatIds(it).length === 0;
      if (catFilter) return itemCatIds(it).includes(catFilter);
      return true;
    });
  }, [items, search, catFilter]);
  const isFiltered = filteredItems.length !== items.length;
  const onSortMove = async (newFilteredIds: string[]) => {
    // Put the reordered visible items back into the slots they occupied in the full list.
    const visible = new Set(filteredItems.map((i) => i.id));
    let k = 0;
    const full = items.map((it) => (visible.has(it.id) ? newFilteredIds[k++] : it.id));
    try {
      await reorder.mutateAsync(full);
      void msg.success(t("menu.sortSaved"));
    } catch {
      void msg.error(t("menu.sortFailed"));
    }
  };
  const catLabel = (it: MenuItem) => {
    const names = itemCatIds(it).map((id) => allCategories.find((c) => c.id === id)).filter(Boolean).map((c) => `${c.emoji ? c.emoji + " " : ""}${c.name}`);
    return names.length ? names.join(" · ") : "—";
  };

  // Sync stall details form when selected stall changes
  useEffect(() => {
    if (selectedStall) {
      stallForm.setFieldsValue({
        name: selectedStall.name,
        description: selectedStall.description,
        is_open: selectedStall.is_open,
        prep_time: selectedStall.prep_time ?? 15,
        auto_accept: selectedStall.auto_accept ?? false,
      });
      setStallDirty(false);
    }
  }, [selectedStall, stallForm]);

  // Always derive optionsItem from live data
  const navigate = useNavigate();
  const optionsItem = optionsItemId ? items.find(it => it.id === optionsItemId) ?? null : null;
  const deleteItem = useDeleteMenuItem();
  const updateItem = useUpdateMenuItem();

  const openCreate = () => { navigate("/menu/items/new", { state: { stallId } }); };
  // Routes to the full add/edit screen (AddMenuItemScreen) instead of the
  // old compact modal, which never had a 定制选项 (customization) section —
  // editing an existing item's option groups was only reachable via the
  // separate "选项组" action, with no way to touch it from "编辑" itself.
  const openEdit = (item: MenuItem) => { navigate("/menu/items/edit", { state: { stallId, item } }); };

  const onDelete = async (id: string, name?: string) => {
    const ok = await confirmDialog({
      title: t("common.delete"),
      description: name ? `${t("menu.deleteConfirm", { defaultValue: "Delete this item?" })} (${name})` : t("menu.deleteConfirm", { defaultValue: "Delete this item?" }),
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel", { defaultValue: "Cancel" }),
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteItem.mutateAsync(id);
      void msg.success(t("menu.deleteSuccess"));
    } catch {
      void msg.error(t("menu.deleteFailed"));
    }
  };

  const onToggleAvailability = async (item: MenuItem) => {
    try {
      await updateItem.mutateAsync({ id: item.id, is_available: !item.is_available });
    } catch {
      void msg.error(t("menu.saveFailed"));
    }
  };

  const onSaveStall = async () => {
    if (!stallId) return;
    try {
      const values = await stallForm.validateFields();
      await updateStall.mutateAsync({ id: stallId, ...values as { name?: string; description?: string; is_open?: boolean } });
      void msg.success(t("stalls.updateSuccess"));
      setStallDirty(false);
    } catch {
      void msg.error(t("stalls.saveFailed"));
    }
  };

  // Categories drawer state
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<MenuCategoryRow | null>(null);
  const [catForm] = Form.useForm();
  const createCat = useCreateMenuCategory();
  const updateCat = useUpdateMenuCategory();
  const deleteCat = useDeleteMenuCategory();

  const saveCat = async () => {
    try {
      const vals = await catForm.validateFields();
      if (editingCat) {
        await updateCat.mutateAsync({ id: editingCat.id, ...vals as Partial<MenuCategoryRow> });
      } else {
        await createCat.mutateAsync({ name: (vals as { name: string }).name, stall_id: stallId, emoji: (vals as { emoji?: string }).emoji, sort_order: (vals as { sort_order?: number }).sort_order });
      }
      void msg.success(t("common.saved"));
      setCatModalOpen(false);
    } catch { void msg.error(t("menu.saveFailed")); }
  };

  // Table columns
  const columns: ColumnsType<MenuItem> = [
    {
      title: <span>{t("menu.colHeaderItemName")}</span>,
      key: "name", width: 240,
      render: (_, row) => {
        const imgUrl = row.image_url;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {imgUrl ? (
              <img className="admin-menu-item-thumbnail" src={imgUrl} alt={row.name} style={{ borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
            ) : (
              <div className="admin-menu-item-thumbnail" style={{ borderRadius: 10, background: T.surfaceSection, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: T.textMuted, flexShrink: 0 }}>
                {row.name.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div className="admin-data-customer">{row.name}</div>
              {row.description && <div className="admin-data-secondary" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 150 }}>{row.description}</div>}
            </div>
          </div>
        );
      },
    },
    {
      title: <span>{t("menu.colHeaderCategory")}</span>,
      key: "category", width: 160,
      render: (_, row) => {
        const r = row as MenuItem & { categories?: Array<{ id: string }>; category_ids?: string[] };
        const catIds = r.categories?.map(c => c.id) ?? r.category_ids ?? [];
        if (!catIds.length) return <Text className="admin-data-secondary">—</Text>;
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {catIds.map(id => {
              const cat = allCategories.find(c => c.id === id);
              return cat ? (
                <span className="admin-data-badge" key={id} style={{
                  display: "inline-block",
                  background: "#F5F5F5",
                  border: "1px solid #E5E7EB",
                  color: T.textBody,
                  whiteSpace: "nowrap",
                }}>
                  {cat.emoji ? `${cat.emoji} ` : ""}{cat.name}
                </span>
              ) : null;
            })}
          </div>
        );
      },
    },
    {
      title: <span>{t("menu.colHeaderPriceRm")}</span>,
      key: "price", width: 120,
      render: (_, row) => {
        const offer = (row as MenuItem & { offer_price?: string }).offer_price;
        return (
          <div>
            <div className="admin-data-amount">RM {Number(row.price).toFixed(2)}</div>
            {offer && <div className="admin-data-secondary" style={{ textDecoration: "line-through" }}>RM {parseFloat(offer).toFixed(2)}</div>}
          </div>
        );
      },
    },
    {
      title: <span>{t("menu.colHeaderAvailability")}</span>,
      key: "available", width: 120,
      render: (_, row) => (
        <Switch
          checked={row.is_available}
          onChange={() => void onToggleAvailability(row)}
          style={{ background: row.is_available ? T.successBright : undefined }}
        />
      ),
    },
    {
      title: <span>{t("menu.colHeaderLastUpdated")}</span>,
      key: "updated_at", width: 150,
      render: (_, row) => {
        const ts = (row as MenuItem & { updated_at?: string }).updated_at;
        if (!ts) return <Text className="admin-data-secondary">—</Text>;
        return <span className="admin-data-time">{formatDateTimeCompact(ts, i18n.language)}</span>;
      },
    },
    {
      title: <span>{t("orders.colAction")}</span>, key: "actions", width: 50, fixed: "right",
      render: (_, row) => {
        const items: MenuProps["items"] = [
          { key: "edit",    label: t("common.edit"),            onClick: () => openEdit(row) },
          { key: "options", label: t("menu.manageOptions"),     onClick: () => setOptionsItemId(row.id) },
          {
            key: "delete",
            label: <span style={{ color: T.dangerText }}>{t("common.delete")}</span>,
            onClick: () => void onDelete(row.id, row.name),
          },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <button type="button" className="admin-data-more" aria-label={t("orders.colAction")}>
              <MoreOutlined />
            </button>
          </Dropdown>
        );
      },
    },
  ];

  const isTablet = viewportWidth <= 1280;
  const useDetailsOverlay = viewportWidth <= 1180;
  const isSmallTablet = viewportWidth <= 1080;
  const isCompactAdminShell = viewportWidth <= 900;
  const adminSidebarWidth = isCompactAdminShell ? 0 : 256;
  const adminHeaderHeight = 64;
  const leftPanelWidth = isCompactAdminShell ? 0 : isSmallTablet ? 160 : isTablet ? 190 : 240;
  const rightPanelWidth = selectedStall && !useDetailsOverlay ? (isTablet ? 260 : 320) : 0;
  const detailsPanelWidth = useDetailsOverlay ? Math.min(340, Math.max(280, viewportWidth - adminSidebarWidth - leftPanelWidth - 16)) : rightPanelWidth;
  const contentPadding = isTablet ? 16 : 24;
  const topBarPadding = isTablet ? "12px 16px" : "16px 24px";

  // One presentation for the desktop rail and compact stall picker.
  const stallList = stalls.map(stall => {
    const isActive = stall.id === stallId;
    const itemCount = isActive ? items.length : null;
    return (
      <button
        type="button"
        className="admin-menu-stall-option"
        aria-pressed={isActive}
        key={stall.id}
        onClick={() => { setStallId(stall.id); setShowStallListOverlay(false); if (useDetailsOverlay) setDetailsOpen(false); }}
        style={{
          display: "flex", alignItems: "center", gap: isSmallTablet ? 8 : 10,
          padding: isSmallTablet ? "10px 12px" : "10px 16px", cursor: "pointer",
          background: isActive ? T.brandLight : "transparent",
          borderLeft: isActive ? "3px solid #F97316" : "3px solid transparent",
          transition: "background 0.15s",
        }}
      >
        {/* Stall avatar */}
        {stall.cover_image ? (
          <img className="admin-menu-stall-avatar" src={stall.cover_image} alt={stall.name} style={{ width: 36, height: 36, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
        ) : (
          <span className="admin-menu-stall-avatar" style={{ width: 36, height: 36, borderRadius: 8, background: isActive ? "#FFEDD5" : T.surfaceSection, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: isActive ? T.brand : T.textMuted, flexShrink: 0 }}>
            {stall.name.slice(0, 1).toUpperCase()}
          </span>
        )}
        <span style={{ display: "block", flex: 1, minWidth: 0 }}>
          <span className="admin-menu-stall-name" style={{ display: "block", fontSize: 13, fontWeight: 600, color: isActive ? T.textDark : T.textBody, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {stall.name}
          </span>
          <span className="admin-menu-stall-meta" style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <StatusBadge
              status={stall.is_open ? "online" : "paused"}
              kind="staff"
              label={stall.is_open ? t("menu.stallBadgeActive") : t("menu.stallBadgePaused")}
              size="xs"
              className="admin-menu-stall-status"
            />
            {itemCount !== null && (
              <span className="admin-menu-stall-picker-count">{t("menu.itemCount", { count: itemCount })}</span>
            )}
          </span>
        </span>
        {itemCount !== null && (
          <span className="admin-menu-stall-rail-count" style={{ background: isActive ? T.brand : T.surfaceSection, color: isActive ? T.surface : T.textMuted, borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
            {itemCount}
          </span>
        )}
        <span className="admin-menu-stall-indicator" aria-hidden="true">
          <Check className="admin-menu-stall-check" size={14} strokeWidth={2.5} />
          <ChevronRight className="admin-menu-stall-chevron" size={16} strokeWidth={1.75} />
        </span>
      </button>
    );
  });

  return (
    <AdminShell>
      <div className="admin-menu admin-full-bleed" style={{ minHeight: `calc(100vh - ${adminHeaderHeight}px)`, background: T.bg }}>

        {/* ── Left stall list panel ── */}
        <div className="admin-menu-stalls" style={{
          position: "fixed",
          left: adminSidebarWidth,
          top: adminHeaderHeight,
          bottom: 0,
          zIndex: 40,
          width: leftPanelWidth,
          borderRight: "1px solid #E5E7EB",
          background: T.surface,
          display: "flex",
          flexDirection: "column",
        }}>
          <div className="flex items-center text-base font-bold uppercase border-b border-[#E5E7EB] h-[58px]" style={{ padding: `0 ${isSmallTablet ? 12 : 16}px` }}>
            {t("menu.yourStalls")}
          </div>
          <div style={{ flex: 1, overflowY: "auto" }}>
            {stallList}
          </div>
        </div>

        {/* ── Center content ── */}
        <div className="admin-menu-workspace" style={{
          position: "fixed",
          left: adminSidebarWidth + leftPanelWidth,
          right: rightPanelWidth,
          top: adminHeaderHeight,
          bottom: 0,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          background: T.bg,
        }}>
          {/* Top bar */}
          <div
            className="admin-menu-toolbar flex items-center justify-between text-xs font-bold uppercase border-b border-[#E5E7EB]"
            style={{
            flexShrink: 0,
            zIndex: 35,
            minHeight: 58,
            padding: topBarPadding,
            background: T.surface,
            gap: 12,
            flexWrap: isTablet ? "wrap" : "nowrap",
          }}>
            <span className="text-lg font-bold" style={{ color: T.textDark, whiteSpace: "nowrap" }}>{t("menu.title")}</span>
	            <div className="admin-menu-actions">
	              {isCompactAdminShell && (
	                <Button
	                  onClick={() => setShowStallListOverlay(true)}
	                  style={{ borderRadius: 10, fontWeight: 700, minHeight: isTablet ? 34 : undefined }}
	                >
	                  {t("menu.yourStalls")}
	                </Button>
	              )}
	              {selectedStall && useDetailsOverlay && (
	                <Button
	                  onClick={() => setDetailsOpen(true)}
	                  style={{ borderRadius: 10, fontWeight: 700, minHeight: isTablet ? 34 : undefined }}
	                >
	                  {t("menu.stallPanelTitle")}
	                </Button>
	              )}
	              <Button
	                icon={<UnorderedListOutlined />}
	                onClick={() => setCatDrawerOpen(true)}
	                disabled={!stallId}
	                style={{ minHeight: isTablet ? 34 : undefined }}
	              >
                {t("menu.manageCategories")}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreate}
                disabled={!stallId}
	                style={{ background: T.brand, borderColor: T.brand, fontWeight: 600, minHeight: isTablet ? 34 : undefined }}
              >
                {t("menu.addItem")}
              </Button>
            </div>
          </div>

          {/* Content area */}
          <div className="admin-menu-items" style={{ flex: 1, minHeight: 0, padding: contentPadding, overflowY: "auto" }}>
            {!stallId ? (
              <div style={{ padding: 60, textAlign: "center", color: "#9CA3AF", fontSize: 15 }}>
                {t("menu.selectStallFromLeft")}
              </div>
            ) : (
              <>
                {/* Stall header */}
	                <div style={{ marginBottom: isTablet ? 12 : 16 }}>
                  <div style={{ fontSize: isTablet ? 18 : 22, fontWeight: 800, color: T.textDark }}>
                    {selectedStall?.cover_image
                      ? <img src={selectedStall.cover_image} style={{ width: 28, height: 28, borderRadius: 6, objectFit: "cover", marginRight: 8, verticalAlign: "middle" }} alt="" />
                      : null
                    }
                    {selectedStall?.name ?? ""}
                  </div>
                  <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
                    {t("menu.managingSummary", {
                      items: t("menu.itemCount", { count: items.length }),
                      categories: t("menu.categoryCount", { count: categories.length }),
                    })}
                  </div>
                </div>

                {/* Items table */}
	                <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 12 }}>
                  <Input.Search
                    value={search}
                    onChange={(e: { target: { value: string } }) => setSearch(e.target.value)}
                    placeholder={t("menu.searchPlaceholder")}
                    style={{ width: 240 }}
                  />
                  <Select
                    value={catFilter}
                    onChange={(v: string | undefined) => setCatFilter(v)}
                    placeholder={t("menu.allCategories")}
                    allowClear
                    style={{ width: 220 }}
                    options={[
                      ...categories.map((c) => ({ value: c.id, label: `${c.emoji ? c.emoji + " " : ""}${c.name}` })),
                      { value: "__none__", label: t("menu.uncategorized") },
                    ]}
                  />
                  <span style={{ color: T.textMuted, fontSize: 13 }}>{filteredItems.length}/{items.length}</span>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    <Button type={viewMode === "list" ? "primary" : "default"} onClick={() => setViewMode("list")}>{t("menu.listView")}</Button>
                    <Button type={viewMode === "sort" ? "primary" : "default"} onClick={() => setViewMode("sort")}>{t("menu.sortView")}</Button>
                  </div>
                </div>

                {viewMode === "sort" ? (
                  <>
                    <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 10 }}>
                      {t("menu.sortHint")}{isFiltered ? ` ${t("menu.sortFilteredHint")}` : ""}
                    </div>
                    <SortableMenuList
                      disabled={reorder.isPending}
                      rows={filteredItems.map((it) => ({
                        id: it.id, name: it.name, price: it.price, image_url: it.image_url,
                        is_featured: it.is_featured, is_available: it.is_available, categoryLabel: catLabel(it),
                      }))}
                      onMove={onSortMove}
                    />
                  </>
                ) : (
                  <div className="admin-menu-table-panel admin-data-table-panel">
                    <DataTable<MenuItem>
                      columns={columns}
                      dataSource={filteredItems}
                      loading={isLoading}
                      rowKey="id"
                      sticky={false}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── Right stall details panel ── */}
        {selectedStall && (
          <>
          {useDetailsOverlay && detailsOpen && (
            <div className="admin-menu-details-backdrop"
              style={{
                position: "fixed",
                left: adminSidebarWidth + leftPanelWidth,
                right: 0,
                top: adminHeaderHeight,
                bottom: 0,
                zIndex: 70,
                background: "rgba(15,23,42,0.18)",
              }}
            />
          )}
          <div className="admin-menu-details" data-open={!useDetailsOverlay || detailsOpen} style={{
            position: "fixed",
            right: 0,
            top: adminHeaderHeight,
            bottom: 0,
            zIndex: useDetailsOverlay ? 80 : 40,
            width: detailsPanelWidth,
            borderLeft: "1px solid #E5E7EB",
            background: T.surface,
            display: "flex",
            flexDirection: "column",
            boxShadow: useDetailsOverlay ? "0 20px 60px rgba(15,23,42,0.18)" : "none",
            transform: useDetailsOverlay && !detailsOpen ? "translateX(100%)" : "translateX(0)",
            transition: "transform 0.22s ease",
          }}>
            {/* Header */}
            <div
	              className="admin-menu-details-header flex items-center justify-between text-base font-bold uppercase border-b border-[#E5E7EB]"
	              style={{ minHeight: 58, padding: `0 ${isTablet ? 16 : 20}px`, color: T.textDark }}>
                <span>{t("menu.stallPanelTitle")}</span>
                {useDetailsOverlay && (
                  <button
                    type="button"
                    onClick={() => setDetailsOpen(false)}
                    className="admin-menu-details-close"
                    style={{ border: 0, background: "transparent", color: T.textMuted, cursor: "pointer", fontSize: 20, lineHeight: 1 }}
                    aria-label="Close"
                  >
                    x
                  </button>
                )}
              </div>

            <div className="admin-menu-details-body" style={{ flex: 1, overflowY: "auto", padding: isTablet ? 16 : 20 }}>
              {/* Avatar — standalone large square */}
              <div style={{ marginBottom: 18 }}>
                <div style={{
	                  width: isTablet ? 54 : 66, height: isTablet ? 54 : 66, borderRadius: 12,
                  background: T.statusPreparingBg, border: "1px solid #FED7AA",
                  display: "flex", alignItems: "center", justifyContent: "center",
	                  fontSize: isTablet ? 24 : 30, overflow: "hidden", marginBottom: 12,
                }}>
                  {selectedStall.cover_image
                    ? <img src={selectedStall.cover_image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    : "🍜"
                  }
                </div>
	                <div style={{ fontSize: isTablet ? 17 : 20, fontWeight: 800, color: T.textDark, lineHeight: 1.2 }}>{selectedStall.name}</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 3 }}>
                  {t("menu.stallIdLabel", { id: selectedStall.id.slice(0, 8).toUpperCase() })}
                </div>
              </div>

              <Form
                form={stallForm}
                layout="vertical"
                onValuesChange={() => setStallDirty(true)}
              >
                {/* Stall Status card */}
                <div style={{ background: T.bg, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{t("menu.stallStatusTitle")}</span>
                    <StatusBadge
                      status={selectedStall.is_open ? "online" : "paused"}
                      kind="staff"
                      label={selectedStall.is_open ? t("menu.stallOnline") : t("menu.stallOffline")}
                    />
                  </div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: T.textBody }}>{t("menu.pauseOrdersHint")}</span>
                    {/* "暂停接单" reads as pause-when-on, but the field it edits is
                        is_open (true = accepting orders) — this admin-ui Form
                        shim has no getValueProps/getValueFromEvent support
                        (that's antd-only), so the switch is bound manually
                        here instead of via Form.Item, inverting the display
                        so ON means paused while is_open in the form stays
                        correct. */}
                    <Switch
                      checked={!(stallIsOpenWatched ?? selectedStall.is_open)}
                      onChange={(checked: boolean) => {
                        stallForm.setFieldValue("is_open", !checked);
                        setStallDirty(true);
                      }}
                      style={{ background: !(stallIsOpenWatched ?? selectedStall.is_open) ? T.successBright : undefined }}
                    />
                  </div>
                </div>

                {/* Preparation Time */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: T.textMuted, letterSpacing: 0.8, textTransform: "uppercase" as const, marginBottom: 8 }}>
                    {t("menu.prepTimeSection")}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Form.Item name="prep_time" noStyle rules={[{ required: true, type: "number", min: 1, max: 120 }]}>
                      <InputNumber
                        min={1} max={120}
                        style={{ flex: 1, borderRadius: 8 }}
                      />
                    </Form.Item>
                    <span style={{ fontSize: 13, color: T.textBody, fontWeight: 500, flexShrink: 0 }}>{t("menu.prepTimeMins")}</span>
                  </div>
                </div>

                {/* Auto-Accept Orders */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  paddingBottom: 18, marginBottom: 18, borderBottom: "1px solid #F3F4F6",
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>{t("menu.autoAcceptTitle")}</div>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{t("menu.autoAcceptHint")}</div>
                  </div>
                  <Form.Item name="auto_accept" valuePropName="checked" noStyle>
                    <Switch style={{ background: undefined }} />
                  </Form.Item>
                </div>

                {/* Opening Hours */}
                <div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: T.textMuted, letterSpacing: 0.8, textTransform: "uppercase" as const, marginBottom: 12 }}>
                    {t("stalls.openingHours")}
                  </div>
                  {[
                    { label: t("stalls.weekdaysHours"), time: t("stalls.hoursWeekdayExample") },
                    { label: t("stalls.weekendHours"), time: t("stalls.hoursWeekendExample") },
                  ].map((row, i, arr) => (
                    <div key={row.label} style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      paddingBottom: 12, marginBottom: i < arr.length - 1 ? 12 : 0,
                      borderBottom: i < arr.length - 1 ? "1px solid #F3F4F6" : "none",
                    }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{row.label}</span>
                      <span style={{ fontSize: 13, color: T.textBody }}>{row.time}</span>
                    </div>
                  ))}
                </div>
              </Form>
            </div>

            {/* Footer buttons */}
            <div className="admin-menu-details-footer" style={{ padding: isTablet ? "12px 16px" : "16px 20px", borderTop: "1px solid #E5E7EB", display: "flex", gap: 10, flexWrap: isTablet ? "wrap" : "nowrap" }}>
              <Button
                block
                style={{ borderRadius: 24, fontWeight: 600, height: 44 }}
                onClick={() => {
                  stallForm.setFieldsValue({
                    name: selectedStall.name,
                    description: selectedStall.description,
                    is_open: selectedStall.is_open,
                    prep_time: selectedStall.prep_time ?? 15,
                    auto_accept: selectedStall.auto_accept ?? false,
                  });
                  setStallDirty(false);
                }}
                disabled={!stallDirty}
              >
                {t("stalls.discard")}
              </Button>
              <Button
                block
                type="primary"
                style={{ borderRadius: 24, fontWeight: 700, height: 44, background: T.successDeep, borderColor: T.successDeep }}
                loading={updateStall.isPending}
                disabled={!stallDirty}
                onClick={() => void onSaveStall()}
              >
                {t("common.saveChanges")}
              </Button>
            </div>
          </div>
          </>
        )}
      </div>

      {/* Compact screens use the same stall choices without a hidden rail. */}
      <Drawer
        open={isCompactAdminShell && showStallListOverlay}
        title={
          <div className="admin-menu-stall-picker-heading">
            <span className="admin-menu-stall-picker-icon" aria-hidden="true"><Store size={22} strokeWidth={1.75} /></span>
            <div>
              <h2>{t("menu.yourStalls")}</h2>
              <p>{t("menu.selectStallFromLeft")}</p>
            </div>
          </div>
        }
        onClose={() => setShowStallListOverlay(false)}
        width={360}
        maskClosable
      >
        <div className="admin-menu-stall-picker">{stallList}</div>
      </Drawer>

      {/* ── Option Groups Drawer ── */}
      <OptionGroupsDrawer
        open={!!optionsItemId}
        item={optionsItem}
        onClose={() => setOptionsItemId(null)}
      />

      {/* ── Categories Drawer ── */}
      <Drawer
        open={catDrawerOpen}
        title={
          <div className="admin-menu-category-heading">
            <span className="admin-menu-category-heading-icon" aria-hidden="true"><UnorderedListOutlined /></span>
            <div>
              <h2>{t("menu.manageCategories")}</h2>
              <p>{selectedStall?.name}</p>
            </div>
          </div>
        }
        onClose={() => setCatDrawerOpen(false)}
        width={480}
      >
        <div className="admin-menu-categories">
          <div className="admin-menu-category-toolbar">
            <span className="admin-menu-category-count">{t("menu.categoryCount", { count: categories.length })}</span>
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => { setEditingCat(null); catForm.resetFields(); setCatModalOpen(true); }}
              style={{ background: T.brandAmber, borderColor: T.brandAmber }}
            >
              {t("menu.addCategory")}
            </Button>
          </div>
          <div className="admin-menu-category-list">
            {categories.map(cat => (
              <div key={cat.id} className="admin-menu-category-row">
                <span className="admin-menu-category-emoji" aria-hidden="true">{cat.emoji || <UnorderedListOutlined />}</span>
                <div className="admin-menu-category-copy">
                  <h3>{cat.name}</h3>
                  <p>{t("menu.categorySortOrder")} · {cat.sort_order}</p>
                </div>
                <div className="admin-menu-category-actions">
                  <Button size="small" icon={<EditOutlined />} aria-label={`${t("common.edit")} ${cat.name}`} title={t("common.edit")} onClick={() => {
                    setEditingCat(cat);
                    catForm.setFieldsValue(cat);
                    setCatModalOpen(true);
                  }} />
                  <Popconfirm title={cat.name ? `${t("common.confirmDelete")} (${cat.name})` : t("common.confirmDelete")} onConfirm={() => void deleteCat.mutateAsync({ id: cat.id })} okType="danger" cancelLabel={t("common.cancel")} confirmLabel={t("common.delete")}>
                    <Button className="admin-menu-category-delete" size="small" danger icon={<DeleteOutlined />} aria-label={`${t("common.delete")} ${cat.name}`} title={t("common.delete")} />
                  </Popconfirm>
                </div>
              </div>
            ))}
            {categories.length === 0 && (
              <div className="admin-menu-category-empty">
                <span aria-hidden="true"><UnorderedListOutlined /></span>
                <h3>{t("menu.categoriesEmptyTitle")}</h3>
                <p>{t("menu.categoriesEmptyHint")}</p>
              </div>
            )}
          </div>
        </div>
      </Drawer>

      <Modal
        open={catModalOpen}
        title={
          <div className="admin-menu-category-heading">
            <span className="admin-menu-category-heading-icon" aria-hidden="true"><EditOutlined /></span>
            <div>
              <h2>{editingCat ? t("menu.editCategory") : t("menu.addCategory")}</h2>
              <p>{selectedStall?.name}</p>
            </div>
          </div>
        }
        onCancel={() => setCatModalOpen(false)}
        onOk={() => void saveCat()}
        okText={t("common.save")}
        okButtonProps={{ loading: createCat.isPending || updateCat.isPending, style: { background: T.brandAmber, borderColor: T.brandAmber } }}
        destroyOnHidden
      >
        <div className="admin-menu-category-form">
          <Form form={catForm} layout="vertical">
            <Form.Item name="emoji" label={t("menu.formCategoryEmoji")} extra={t("menu.categoryEmojiHint")} className="admin-menu-category-emoji-field">
              <Input placeholder={t("menu.formCategoryEmojiPlaceholder")} maxLength={4} />
            </Form.Item>
            <Form.Item name="name" label={t("menu.formCategoryName")} rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="sort_order" label={t("menu.formSortOrder")} initialValue={0}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Form>
        </div>
      </Modal>
    </AdminShell>
  );
}
